import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AppState, Tab } from '../../../../state/app.state';
import { Store } from '@ngrx/store';
import { selectActiveSideTab, selectActiveTabDetails, selectActiveTabID, selectMainState, selectTabList } from '../../../../state/selectors';
import { changeActiveSideTab } from '../../../../state/action';

@Component({
  selector: 'app-results-sidebar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent {
  isLoading$!: Observable<boolean>;
  sidebarItems: {
    [key: string]: {
      display: string;
      value: string;
      requiresInternalUser?: boolean;
      subOptions?: { display: string; value: string; showOnlyForCompanyTypes?: string[] }[]
    }[]
  } = {
      'Company View': [
        { display: 'Company Overview', value: 'executive_summary', subOptions: [
          { display: 'Executive Summary', value: 'executive_summary' },
          { display: 'Signal History', value: 'innov_metrics_history', showOnlyForCompanyTypes: ['Public Company', 'Public Investment Firm'] },
          { display: 'Innovation Activity', value: 'innovation_activity_over_time' },
        ] },
        {
          display: 'Competitive Benchmarking', value: 'competitive_benchmarking', subOptions: [
            { display: 'Public Competitors', value: 'company_public_competitors' },
            { display: 'Private Competitors', value: 'company_private_competitors' },
            { display: 'Custom Peer Group', value: 'competitive_benchmarking_peergroup' },
          ]
        },
        {
          display: 'Technology Overview', value: 'tech_overview', subOptions: [
            { display: 'Technology Summary', value: 'company_technology_summary' },
            { display: 'Inventions View', value: 'inventions_view' },

          ]
        },
      ],
      'Thematic View': [
        {
          display: 'Theme Overview', value: '', subOptions: [
            { display: 'Executive Summary', value: 'thematic_exec_summary' },
            { display: 'Performance History', value: 'thematic_performance_history' },
            { display: 'Innovation Activity', value: 'thematic_innovation_activity' },
          ]
        },
        {
          display: 'Competitive Benchmarking', value: 'competitive_benchmarking', subOptions: [
            { display: 'Public Competitors', value: 'thematic_public_competitors' },
            { display: 'Private Competitors', value: 'thematic_private_competitors' },
            { display: 'Custom Peer Group', value: 'thematic_custom_peer_group' },
          ]
        },
        {
          display: 'Technology Overview', value: 'tech_overview', subOptions: [
            { display: 'Technology Summary', value: 'thematic_technology_summary' },
            { display: 'Inventions View', value: 'inventions_view' },
          ]
        },
      ],
    };

  isInternalUser: any;

  formatSectionId(section: string): string {
    return section.replace(/\s+/g, '-');
  }

  objectKeys = Object.keys;

  private getHiddenSideTabs(companyType: string): string[] {
    const hidden: string[] = [];
    for (const items of Object.values(this.sidebarItems)) {
      for (const item of items) {
        for (const sub of item.subOptions ?? []) {
          if (sub.showOnlyForCompanyTypes && !sub.showOnlyForCompanyTypes.includes(companyType)) {
            hidden.push(sub.value);
          }
        }
      }
    }
    return hidden;
  }

  activeTabId$!: Observable<string>;
  activeSideTab$!: Observable<string | null>;
  activeSearchType$!: Observable<string | undefined>;
  activeCompanyType: string | undefined;
  tabList$!: Observable<Tab[]>;

  constructor(private store: Store<AppState>) { }
  private subscriptions: Subscription = new Subscription();

  roles: string | null = null;

  ngOnInit() {
    
    this.roles = JSON.parse(localStorage.getItem('roles')!);
    this.isInternalUser = this.roles!.includes('alpha');

    this.activeTabId$ = this.store.select(selectActiveTabID);
    this.activeSideTab$ = this.store.select(selectActiveSideTab);

    this.activeSearchType$ = this.activeTabId$.pipe(
      switchMap(activeTabId => {
        return this.store.select(selectTabList).pipe(
          map(tabs => {
            const activeTab = tabs.find(tab => tab.tabId === activeTabId);
            return activeTab ? activeTab.search_type : undefined;
          })
        );
      })
    );
    this.subscriptions.add(
      combineLatest([
        this.store.select(selectActiveTabDetails).pipe(map(tab => tab?.params?.entity_data?.companytype)),
        this.activeSideTab$
      ]).subscribe(([type, currentSideTab]) => {
        this.activeCompanyType = type;
        if (currentSideTab && type) {
          const hiddenTab = this.getHiddenSideTabs(type);
          if (hiddenTab.includes(currentSideTab)) {
            this.store.dispatch(changeActiveSideTab({ sideTab: 'executive_summary' }));
          }
        }
      })
    );
    this.tabList$ = this.store.select(selectTabList).pipe(
      map(tabs => tabs.filter(tab => !!tab)) // Filter for non-empty tabs
    );
    this.isLoading$ = this.activeTabId$.pipe(
      switchMap(activeTabId => {
        return this.store.select(selectTabList).pipe(
          map(tabs => {
            const activeTab = tabs.find(tab => tab.tabId === activeTabId);
            return activeTab?.isLoading || false;
          })
        );
      })
    );


    this.subscriptions.add(this.activeTabId$.subscribe());
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }


  onSidebarOptionClick(event: MouseEvent, option: string): void {
    event.preventDefault();
    this.isLoading$.pipe(take(1)).subscribe(isLoading => {
      if (isLoading&&( option==="competitive_benchmarking_peergroup" || option==="executive_summary")) {
        return;
      }
      this.store.dispatch(changeActiveSideTab({ sideTab: option }));
    });
  }

  isActive(value: string): boolean {
    let activeTab: string | null = null;

    this.activeSideTab$.subscribe(tab => {
      activeTab = tab;
    });

    return activeTab === value;
  }

}
